// src/Message/useMessage.tsx
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import { ConfigContext } from "antd/es/config-provider";
import warning from "antd/es/_util/warning";
import classNames from "classnames";
import { useNotification as useRcNotification } from "rc-notification";
import * as React from "react";
import { PureContent } from "./PurePanel";
import useStyle from "./style";
import { getMotion, wrapPromiseFn } from "./util";
var DEFAULT_OFFSET = 8;
var DEFAULT_DURATION = 3;
var Holder = React.forwardRef((props, ref) => {
  const {
    top,
    prefixCls: staticPrefixCls,
    getContainer: staticGetContainer,
    maxCount,
    duration = DEFAULT_DURATION,
    transitionName,
    onAllRemoved
  } = props;
  const { getPrefixCls, getPopupContainer, direction } = React.useContext(ConfigContext);
  const prefixCls = staticPrefixCls || getPrefixCls("message");
  const [, hashId] = useStyle(prefixCls);
  const getStyle = () => ({
    left: "50%",
    transform: "translateX(-50%)",
    top: top != null ? top : DEFAULT_OFFSET
  });
  const getClassName = () => classNames(hashId, direction ? `${prefixCls}-rtl` : "");
  const getNotificationMotion = () => getMotion(prefixCls, transitionName);
  const mergedCloseIcon = /* @__PURE__ */ React.createElement("span", { className: `${prefixCls}-close-x` }, /* @__PURE__ */ React.createElement(CloseOutlined, { className: `${prefixCls}-close-icon` }));
  const [api, holder] = useRcNotification({
    prefixCls,
    style: getStyle,
    className: getClassName,
    motion: getNotificationMotion,
    closable: false,
    closeIcon: mergedCloseIcon,
    duration,
    getContainer: () => (staticGetContainer == null ? void 0 : staticGetContainer()) || (getPopupContainer == null ? void 0 : getPopupContainer()) || document.body,
    maxCount,
    onAllRemoved
  });
  React.useImperativeHandle(ref, () => ({
    ...api,
    prefixCls,
    hashId
  }));
  return holder;
});
var keyIndex = 0;
function useInternalMessage(messageConfig) {
  const holderRef = React.useRef(null);
  const wrapAPI = React.useMemo(() => {
    const close = (key) => {
      var _a;
      (_a = holderRef.current) == null ? void 0 : _a.close(key);
    };
    const open = (config) => {
      if (!holderRef.current) {
        warning(
          false,
          "Message",
          "You are calling notice in render which will break in React 18 concurrent mode. Please trigger in effect instead."
        );
        const fakeResult = () => {
        };
        fakeResult.then = () => {
        };
        return fakeResult;
      }
      const { open: originOpen, prefixCls, hashId } = holderRef.current;
      const noticePrefixCls = `${prefixCls}-notice`;
      const { content, icon, type, key, className, onClose, ...restConfig } = config;
      let mergedKey = key;
      if (mergedKey === void 0 || mergedKey === null) {
        keyIndex += 1;
        mergedKey = `antd-message-${keyIndex}`;
      }
      return wrapPromiseFn((resolve) => {
        originOpen({
          ...restConfig,
          key: mergedKey,
          content: /* @__PURE__ */ React.createElement(PureContent, { prefixCls, type, icon }, content),
          placement: "top",
          className: classNames(
            type && `${noticePrefixCls}-${type}`,
            hashId,
            className
          ),
          onClose: () => {
            onClose == null ? void 0 : onClose();
            resolve();
          }
        });
        return () => {
          close(mergedKey);
        };
      });
    };
    const destroy = (key) => {
      var _a;
      if (key !== void 0) {
        close(key);
      } else {
        (_a = holderRef.current) == null ? void 0 : _a.destroy();
      }
    };
    const clone = {
      open,
      destroy
    };
    const keys = [
      "info",
      "success",
      "warning",
      "error",
      "loading"
    ];
    keys.forEach((type) => {
      const typeOpen = (jointContent, duration, onClose) => {
        let config;
        if (jointContent && typeof jointContent === "object" && "content" in jointContent) {
          config = jointContent;
        } else {
          config = {
            content: jointContent
          };
        }
        let mergedDuration;
        let mergedOnClose;
        if (typeof duration === "function") {
          mergedOnClose = duration;
        } else {
          mergedDuration = duration;
          mergedOnClose = onClose;
        }
        const mergedConfig = {
          onClose: mergedOnClose,
          duration: mergedDuration,
          ...config,
          type
        };
        return open(mergedConfig);
      };
      clone[type] = typeOpen;
    });
    return clone;
  }, []);
  return [
    wrapAPI,
    /* @__PURE__ */ React.createElement(Holder, { key: "message-holder", ...messageConfig, ref: holderRef })
  ];
}
function useMessage(messageConfig) {
  return useInternalMessage(messageConfig);
}
export {
  useMessage as default,
  useInternalMessage
};
